import { css, Button, tokens, formatTime, formatDate, timestampToDateString, timestampToTimeString } from "@balabalabom/ui";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { WeatherOverlay } from "../components/WeatherOverlay";
import { getWeatherCondition, type WeatherCondition } from "../lib/weather";
import { parseDate } from "../lib/date";
import { Link } from "@balabalabom/ui";
import { eventService } from "../lib/getClient";
import { Event as GrpcEvent } from "../generated/events/v1/event_pb";
import { EventKindBadge, eventKindPalette } from "../components/EventKindBadge";
import { resolveEventKind, type EventKind } from "../lib/eventKind";

interface Event {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  address: string;
  image: string;
  details: string;
  requirements: string;
  targetAge: string;
  maxParticipants: number;
  registrationFee: number;
  facebookLink: string;
  registrationOpen: boolean;
  type?: EventKind;
}

export function Events() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weather, setWeather] = useState<WeatherCondition | null>(null);
  const [kindFilter, setKindFilter] = useState<"all" | EventKind>("all");

  // Helper function to convert gRPC Event.AsObject to component Event interface
  const mapGrpcEventToEvent = (grpcEvent: GrpcEvent.AsObject): Event => {
    // Prefer datetime (single source of truth); fallback to date/time for legacy rows
    const eventDate = grpcEvent.datetime ? timestampToDateString(grpcEvent.datetime) : grpcEvent.date || "";
    const eventTime = grpcEvent.datetime ? timestampToTimeString(grpcEvent.datetime) : grpcEvent.time || "";
    return {
      id: grpcEvent.id,
      title: grpcEvent.title,
      date: eventDate,
      time: eventTime,
      location: grpcEvent.location,
      address: grpcEvent.address,
      image: grpcEvent.imageUrl,
      details: grpcEvent.details,
      requirements: grpcEvent.requirements,
      targetAge: grpcEvent.targetAge,
      maxParticipants: grpcEvent.maxParticipants,
      registrationFee: grpcEvent.registrationFee,
      facebookLink: grpcEvent.facebookLink,
      registrationOpen: grpcEvent.registrationOpen,
      type: resolveEventKind({
        title: grpcEvent.title,
        details: grpcEvent.details,
      }),
    };
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const grpcEvents = await eventService.listEvents("all");
        const mappedEvents = grpcEvents.map(mapGrpcEventToEvent);
        
        setEvents(mappedEvents);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An error occurred";
        setError(errorMessage);
        console.error("Error fetching events from gRPC:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Function to calculate event status based on date
  const getEventStatus = (eventDate: string): "current" | "future" | "past" => {
    const today = new Date();
    const eventDateObj = parseDate(eventDate);

    // Set time to start of day for accurate comparison
    today.setHours(0, 0, 0, 0);
    eventDateObj.setHours(0, 0, 0, 0);

    const timeDiff = eventDateObj.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysDiff < 0) {
      return "past";
    } else if (daysDiff <= 7) {
      return "current";
    } else {
      return "future";
    }
  };

  // Fetch weather for the next coming event (fallback to soonest future)
  useEffect(() => {
    if (!events.length) {
      setWeather(null);
      return;
    }
    const current = events.find((e) => getEventStatus(e.date) === "current");
    const target =
      current ||
      events
        .filter((e) => getEventStatus(e.date) === "future")
        .sort(
          (a, b) => parseDate(a.date).getTime() - parseDate(b.date).getTime()
        )[0];
    if (!target) {
      setWeather(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const condition = await getWeatherCondition(
          [target.address, target.location].filter(Boolean),
          target.date
        );
        if (!cancelled) setWeather(condition);
      } catch {
        if (!cancelled) setWeather(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [events]);

  const sortByDate = (
    list: Event[],
    order: "asc" | "desc" = "asc"
  ): Event[] => {
    return [...list].sort((a, b) => {
      const result = parseDate(a.date).getTime() - parseDate(b.date).getTime();
      return order === "asc" ? result : -result;
    });
  };

  const deriveShortDetails = (event: Event): string => {
    const sentences = event.details
      .split(/(?<=[.!?])\s+/)
      .map((sentence) => sentence.trim())
      .filter(Boolean);

    if (!sentences.length) {
      return "";
    }

    return sentences.slice(0, 4).join(" ");
  };

  const getMapsUrl = (e: Event) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      e.address || e.location
    )}`;

  const getCardBackground = (
    event: Event,
    index: number,
    fallback: string | string[]
  ) => {
    const kind = resolveEventKind(event);
    const palette = eventKindPalette(kind);
    const fallbackPalette = Array.isArray(fallback) ? fallback : [fallback];
    if (palette?.length) {
      return palette[index % palette.length];
    }
    return fallbackPalette[index % fallbackPalette.length];
  };

  if (loading) {
    return (
      <main
        className="main-mobile"
        style={css({
          maxWidth: "80rem",
          margin: "0 auto",
          textAlign: "center",
        })}
      >
        <div
          style={css({
            fontSize: "1.25rem",
            color: "#c85a3a",
            marginBottom: "1rem",
          })}
        >
          Loading events...
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main
        className="main-mobile"
        style={css({
          maxWidth: "80rem",
          margin: "0 auto",
          textAlign: "center",
        })}
      >
        <div
          style={css({
            fontSize: "1.25rem",
            color: "#ef4444",
            marginBottom: "1rem",
          })}
        >
          Error loading events: {error}
        </div>
        <Button variant="primary" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </main>
    );
  }

  const currentEvents = sortByDate(
    events.filter((event) => getEventStatus(event.date) === "current"),
    "asc"
  ).filter(
    (event) => kindFilter === "all" || resolveEventKind(event) === kindFilter
  );
  const futureEvents = sortByDate(
    events.filter((event) => getEventStatus(event.date) === "future"),
    "asc"
  ).filter(
    (event) => kindFilter === "all" || resolveEventKind(event) === kindFilter
  );
  const pastEvents = sortByDate(
    events.filter((event) => getEventStatus(event.date) === "past"),
    "desc"
  ).filter(
    (event) => kindFilter === "all" || resolveEventKind(event) === kindFilter
  );

  const filterChipStyle = (active: boolean) =>
    css({
      padding: "0.4rem 0.85rem",
      borderRadius: "0.5rem",
      border: `1px solid ${active ? tokens.colors.brand.accent : tokens.colors.neutral[300]}`,
      backgroundColor: active ? tokens.colors.brand.accent : "#fff",
      color: active ? "#fff" : tokens.colors.brand.deep,
      fontSize: "0.8125rem",
      fontWeight: 600,
      cursor: "pointer",
    });

  return (
    <main
      className="main-mobile"
      style={css({ maxWidth: "80rem", margin: "0 auto" })}
    >
      <div
        style={css({
          display: "flex",
          flexWrap: "wrap",
          gap: "0.5rem",
          marginBottom: "1.5rem",
          alignItems: "center",
        })}
      >
        <span
          style={css({
            fontSize: "0.875rem",
            fontWeight: 600,
            color: tokens.colors.brand.deep,
            marginRight: "0.25rem",
          })}
        >
          Show:
        </span>
        <button
          type="button"
          style={filterChipStyle(kindFilter === "all")}
          onClick={() => setKindFilter("all")}
        >
          All events
        </button>
        <button
          type="button"
          style={filterChipStyle(kindFilter === "storytime")}
          onClick={() => setKindFilter("storytime")}
        >
          Storytime
        </button>
        <button
          type="button"
          style={filterChipStyle(kindFilter === "diy")}
          onClick={() => setKindFilter("diy")}
        >
          DIY
        </button>
      </div>

      {/* Current Event Section */}
      <section className="section-mobile">
        <h2 className="heading-mobile">Next Coming Event</h2>
          {currentEvents.map((event, index) => (
            <div
              key={event.id}
              style={css({ position: "relative", marginBottom: "2rem" })}
            >
              <div
                className="event-card-mobile"
                style={css({
                  backgroundColor: getCardBackground(
                    event,
                    index,
                    tokens.colors.surfaces.eventHighlight
                  ),
                  position: "relative",
                })}
              >
                <div className="event-content-mobile">
                  <div className="event-info-mobile">
                    <div style={css({ marginBottom: "0.5rem" })}>
                      <EventKindBadge event={event} />
                    </div>
                    <h3 className="event-title-mobile">{event.title}</h3>
                    <p className="event-date-mobile">
                      {formatDate(event.date)} at {formatTime(event.time)}
                    </p>
                    <p className="event-location-mobile">
                      📍{" "}
                      <Link href={getMapsUrl(event)} external>
                        {event.location}
                      </Link>
                    </p>
                    <p className="event-details-mobile">
                      {deriveShortDetails(event)}
                    </p>
                    <div className="button-group-mobile">
                      {event.registrationOpen &&
                        event.registrationFee === 0 && (
                        <Button
                          variant="primary"
                          size="md"
                          onClick={() => navigate(`/event/${event.id}#event-registration`)}
                          className="button-full-mobile"
                        >
                          Register
                        </Button>
                      )}
                      {event.registrationOpen &&
                        event.registrationFee !== 0 &&
                        event.facebookLink?.trim() && (
                        <Button
                          variant="primary"
                          size="md"
                          onClick={() =>
                            window.open(event.facebookLink, "_blank")
                          }
                          className="button-full-mobile"
                        >
                          Register
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="md"
                        onClick={() => navigate(`/event/${event.id}`)}
                        className="button-full-mobile"
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                  <div
                    className="event-image-mobile"
                    style={css({ position: "relative" })}
                  >
                    <img
                      src={event.image}
                      alt={event.title}
                      className="image-mobile"
                    />
                    {weather && <WeatherOverlay condition={weather} />}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </section>

      {/* Future Events Section */}
      <section className="section-mobile">
        <h2 className="heading-mobile">Future Events</h2>
        {(() => {
          const isSingle = futureEvents.length === 1;
          const items = isSingle ? [...futureEvents, null, null] : futureEvents;
          return (
            <div className={isSingle ? "grid-three" : "grid-mobile"}>
              {items.map((event, index) => {
                if (!event) {
                  return (
                    <div
                      key={`placeholder-future-${index}`}
                      className="placeholder-card-mobile"
                    >
                      More events coming soon
                    </div>
                  );
                }
                const colors = [
                  tokens.colors.surfaces.eventHighlight,
                  "#5a7ab8",
                  "#f4e4b8",
                ];
                return (
                  <div
                    key={event.id}
                    className="event-card-mobile"
                    style={css({
                      backgroundColor: getCardBackground(event, index, colors),
                      overflow: "hidden",
                    })}
                  >
                    <div style={css({ aspectRatio: "4 / 3" })}>
                      <img
                        src={event.image}
                        alt={event.title}
                        className="image-mobile"
                        style={css({ aspectRatio: "unset" })}
                      />
                    </div>
                    <div className="card-content-mobile">
                      <div style={css({ marginBottom: "0.5rem" })}>
                        <EventKindBadge event={event} />
                      </div>
                      <h3 className="card-title-mobile">{event.title}</h3>
                      <p className="card-date-mobile">
                        {formatDate(event.date)}
                      </p>
                      <p className="card-meta-mobile">
                        {formatTime(event.time)} • {event.targetAge}
                      </p>
                      <p className="card-meta-mobile">
                        📍{" "}
                        <Link href={getMapsUrl(event)} external>
                          {event.location}
                        </Link>
                      </p>
                      <div className="button-group-mobile">
                        {event.registrationOpen &&
                          event.registrationFee === 0 && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() =>
                              navigate(`/event/${event.id}#event-registration`)
                            }
                            className="button-full-mobile"
                          >
                            Register
                          </Button>
                        )}
                        {event.registrationOpen &&
                          event.registrationFee !== 0 &&
                          event.facebookLink?.trim() && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() =>
                              window.open(event.facebookLink, "_blank")
                            }
                            className="button-full-mobile"
                          >
                            Register
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/event/${event.id}`)}
                          className="button-full-mobile"
                        >
                          Details
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </section>

      {/* Past Events Section */}
      <section>
        <h2 className="heading-mobile">Past Events</h2>
        {(() => {
          const isSingle = pastEvents.length === 1;
          const items = isSingle ? [...pastEvents, null, null] : pastEvents;
          return (
            <div className={isSingle ? "grid-three" : "grid-mobile"}>
              {items.map((event, index) => {
                if (!event) {
                  return (
                    <div
                      key={`placeholder-past-${index}`}
                      className="placeholder-card-mobile"
                    >
                      More past events will appear here
                    </div>
                  );
                }
                const colors = [
                  "#f4d4a8",
                  "#b8d8e8",
                  tokens.colors.surfaces.eventHighlight,
                ];
                return (
                  <div
                    key={event.id}
                    className="event-card-mobile"
                    style={css({
                      backgroundColor: getCardBackground(event, index, colors),
                      overflow: "hidden",
                    })}
                  >
                    <div style={css({ aspectRatio: "4 / 3" })}>
                      <img
                        src={event.image}
                        alt={event.title}
                        className="image-mobile"
                        style={css({ aspectRatio: "unset" })}
                      />
                    </div>
                    <div className="card-content-mobile">
                      <div style={css({ marginBottom: "0.5rem" })}>
                        <EventKindBadge event={event} />
                      </div>
                      <h3
                        className="card-title-mobile"
                        style={css({ fontSize: "1rem" })}
                      >
                        {event.title}
                      </h3>
                      <p className="card-meta-mobile">
                        {formatDate(event.date)}
                      </p>
                      <p className="card-meta-mobile">
                        📍{" "}
                        <Link href={getMapsUrl(event)} external>
                          {event.location}
                        </Link>
                      </p>
                      <div className="button-group-mobile">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/event/${event.id}`)}
                          className="button-full-mobile"
                        >
                          Details
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </section>
    </main>
  );
}
